import { openDB } from "idb"

const DB_NAME = "jewlr-images"
const STORE_NAME = "images"
const DB_VERSION = 1
const keyPrefix = "_jwl_"
const defaultDays = 1

// Resolve to null on failure (e.g. IndexedDB disabled in private browsing)
// so callers can guard with a simple null-check instead of try/catch.
const dbPromise =
  typeof window !== "undefined" && window.indexedDB
    ? openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          db.createObjectStore(STORE_NAME, { keyPath: "key" })
        },
      }).catch((e) => {
        console.error(
          "IndexedDBCache: failed to open DB, falling back to in-memory cache",
          e
        )
        return null
      })
    : Promise.resolve(null)

// Fallback when IndexedDB is unavailable (e.g. private browsing). Data is
// lost on page unload.
const memoryCache = new Map()

const pkey = (key) => `${keyPrefix}${key}`

// TTL is stored in a separate lightweight "_meta" record so exist() and
// expiry checks never load the multi-megabyte image value into memory.
const metaKey = (key) => `${pkey(key)}_meta`

const ttlTimestamp = () => Date.now() + defaultDays * 24 * 3600 * 1000

const readMemoryEntry = (key) => {
  const entry = memoryCache.get(pkey(key))
  if (!entry) return null
  if (entry.ttl <= Date.now()) {
    memoryCache.delete(pkey(key))
    return null
  }
  return entry
}

const store = async (key, data) => {
  const db = await dbPromise
  if (!db) {
    memoryCache.set(pkey(key), { ttl: ttlTimestamp(), value: data })
    return
  }
  try {
    const tx = db.transaction(STORE_NAME, "readwrite")
    await Promise.all([
      tx.store.put({ key: pkey(key), value: data }),
      tx.store.put({ key: metaKey(key), ttl: ttlTimestamp() }),
      tx.done,
    ])
  } catch (e) {
    console.error("IndexedDBCache store failed", e)
  }
}

const get = async (key) => {
  const db = await dbPromise
  if (!db) {
    return readMemoryEntry(key)?.value ?? null
  }
  try {
    const meta = await db.get(STORE_NAME, metaKey(key))
    if (!meta || meta.ttl <= Date.now()) {
      if (meta) {
        const tx = db.transaction(STORE_NAME, "readwrite")
        await Promise.all([
          tx.store.delete(pkey(key)),
          tx.store.delete(metaKey(key)),
          tx.done,
        ])
      }
      return null
    }
    const entry = await db.get(STORE_NAME, pkey(key))
    return entry?.value ?? null
  } catch (e) {
    console.error("IndexedDBCache get failed", e)
    return null
  }
}

const exist = async (key) => {
  const db = await dbPromise
  if (!db) return !!readMemoryEntry(key)
  try {
    const meta = await db.get(STORE_NAME, metaKey(key))
    return !!meta && meta.ttl > Date.now()
  } catch (e) {
    console.error("IndexedDBCache exist failed", e)
    return false
  }
}

const remove = async (key) => {
  const db = await dbPromise
  if (!db) {
    memoryCache.delete(pkey(key))
    return
  }
  try {
    const tx = db.transaction(STORE_NAME, "readwrite")
    await Promise.all([
      tx.store.delete(pkey(key)),
      tx.store.delete(metaKey(key)),
      tx.done,
    ])
  } catch (e) {
    console.error("IndexedDBCache remove failed", e)
  }
}

const IndexedDBCache = { exist, get, remove, store }
export default IndexedDBCache
